// public/v3/src/lib/ccm-tier-bump.js — pure CCM billing-tier auto-bump helpers.
//
// Lives separately from billing-generate.js (ESM, not Jest-loadable) so Jest can
// require() it directly, same as bhi-billing.js. Pure functions only — no React,
// no fetches. billing-generate.js and push-emr.js import from here via ESM interop.
//
// Feature: a patient enrolled in Standard CCM (99490) who logs >= 90 CCM minutes
// in a month may, on explicit operator affirmation, bill a higher tier:
//   - 99487 (Complex CCM)      — affirm moderate-to-high MDM
//   - 99491 (Practitioner-Led) — affirm >= 50% physician personal time
// The affirmation is stored per-month on patient.ccmTierMonth[monthStr].

const { entryProgramFamily } = require('./bhi-billing');

const TIER_BUMP_MIN_MINUTES = 90;

// Credentials that count as physician/QHP personal time for the 99491 hint.
const PROVIDER_CREDENTIALS = new Set(['MD', 'DO', 'NP', 'PA', 'CNS']);

function _raw(patient) {
  return patient && patient._raw ? patient._raw : (patient || {});
}

// Credential recorded for an entry: the entry's own credential wins; otherwise
// look the staff name up in staffList. Mirrors billing-generate.js _formatEntryStaff.
function _entryCredential(entry, staffList) {
  if (entry && entry.credential) return entry.credential;
  const name = String((entry && (entry.staff || entry.staffName || entry.createdByName || entry.authorName)) || '')
    .trim().toLowerCase();
  if (!name) return 'none';
  const list = Array.isArray(staffList) ? staffList : [];
  const match = list.find((s) => String((s && s.name) || '').toLowerCase() === name);
  return (match && match.credential) || 'none';
}

// Provider-vs-total CCM minutes for the month. Returns { totalMin, providerMin, pct }
// with pct in 0..1. Advisory only — never auto-affirms 99491.
function physicianTimePct(patient, monthStr, staffList) {
  const p = _raw(patient);
  const log = Array.isArray(p.activityLog) ? p.activityLog : [];
  let totalMin = 0;
  let providerMin = 0;
  for (const e of log) {
    if (!e || e.isConsolidatedNote) continue;
    if (String(e.date || '').indexOf(monthStr) !== 0) continue;
    if (entryProgramFamily(e) !== 'ccm') continue;
    const dur = parseInt(e.duration, 10) || 0;
    totalMin += dur;
    if (PROVIDER_CREDENTIALS.has(_entryCredential(e, staffList))) providerMin += dur;
  }
  return { totalMin, providerMin, pct: totalMin > 0 ? providerMin / totalMin : 0 };
}

// Candidate = enrolled Standard CCM (99490), no APCM fallback for the month,
// and >= 90 CCM minutes logged.
function isTierBumpCandidate(patient, monthStr, minutes) {
  const p = _raw(patient);
  if (String((p.billing || {}).program || '') !== '99490') return false;
  if (p.apcmMonth && p.apcmMonth[monthStr] && p.apcmMonth[monthStr].code) return false;
  return (minutes != null ? minutes : 0) >= TIER_BUMP_MIN_MINUTES;
}

// Map an affirmation to the billed code. MDM wins over physician-time (Complex
// out-pays Practitioner-Led for all time >= 90 min). Returns { code, basis } | null.
function resolveAffirmedTier(affirm) {
  const a = affirm || {};
  if (a.mdm) return { code: '99487', basis: 'mdm' };
  if (a.physicianTime) return { code: '99491', basis: 'practitioner-led' };
  return null;
}

// Single source of truth: which CCM code bills this month.
// Precedence: APCM fallback > affirmed ccmTierMonth (>=90 guard) > enrolled program.
// `minutes` enables auto-revert: a stored bump is ignored once the month slips
// below 90 CCM minutes. Pass null to skip the guard (display-only callers).
function effectiveCcmCodeForMonth(patient, monthStr, minutes) {
  const p = _raw(patient);
  const apcm = (p.apcmMonth && p.apcmMonth[monthStr]) || null;
  if (apcm && apcm.code) return String(apcm.code);
  const bump = (p.ccmTierMonth && p.ccmTierMonth[monthStr]) || null;
  if (bump && bump.code && (minutes == null || minutes >= TIER_BUMP_MIN_MINUTES)) return String(bump.code);
  return String((p.billing && p.billing.program) || '');
}

module.exports = {
  TIER_BUMP_MIN_MINUTES,
  PROVIDER_CREDENTIALS,
  physicianTimePct,
  isTierBumpCandidate,
  resolveAffirmedTier,
  effectiveCcmCodeForMonth,
};
